import { useDispatch, useSelector } from "react-redux";
import {
  selectCachedCharacterPartRenderByName,
  selectSelectedCharacterPartDetails,
  setCachedCharacterPartRender,
} from "../../../app/slices/CharacterPartsSlice";
import PiButton from "../../../components/PiButton";
import PropLibraryInteractionLabel from "../../prop_library/PropLibraryInteractionLabel";
import ClothesIcon from "../../../icons/ClothesIcon";
import { useEffect, useState } from "react";
import { RootState } from "../../../app/Store";
import { selectCurrentOrganization } from "../../../app/slices/UserSlice";
import { CharacterPartService } from "../../../services/CharacterPartService";
export default function CharacterPartLibraryDetailsColumn(props: {
  onSubmit: () => void;
}) {
  const { onSubmit } = { ...props };
  const dispatch = useDispatch();
  const details = useSelector(selectSelectedCharacterPartDetails);
  const currentOrganization = useSelector(selectCurrentOrganization);
  const renderUri = useSelector((state: RootState) =>
    details ? selectCachedCharacterPartRenderByName(state, details.name) : undefined
  );
  const [isFetchingRender, setIsFetchingRender] = useState(false);
  const [isImageLoaded, setIsImageLoaded] = useState(false);
  const [hasImageError, setHasImageError] = useState(false);

  useEffect(() => {
    setIsImageLoaded(false);
    setHasImageError(false);
  }, [renderUri, details?.name]);

  useEffect(() => {
    if (!details?.name) return;
    if (!currentOrganization?.id) return;
    if (renderUri) return;

    const loadRender = async () => {
      setIsFetchingRender(true);
      const render = await CharacterPartService.GetCharacterPartGenderedPairRender(
        currentOrganization.id,
        details.name
      );
      if (render?.downloadUri) {
        dispatch(
          setCachedCharacterPartRender({
            characterPartName: details.name,
            downloadUri: render.downloadUri,
          })
        );
      }
      setIsFetchingRender(false);
    };

    loadRender();
  }, [currentOrganization?.id, details?.name, dispatch, renderUri]);

  const noImage = (
    <div className="h-[96px] piLg:h-[256px] w-full flex">
      <div className="m-auto text-[96px] text-creator-text-soft">
        <ClothesIcon size={96} />
      </div>
    </div>
  );

  const image = renderUri && !hasImageError && (
    <div className="relative h-[96px] piLg:h-[256px] w-full overflow-hidden">
      {((isFetchingRender && !renderUri) ||
        (!!renderUri && !isImageLoaded && !hasImageError)) && (
        <div className="absolute inset-0 animate-pulse bg-creator-surface" />
      )}
      <img
        src={renderUri}
        alt={details?.name ?? "Character part render"}
        className={`h-full w-full object-contain rounded-[5px] p-[8px] transition-opacity ${
          isImageLoaded ? "opacity-100" : "opacity-0"
        }`}
        onLoad={() => setIsImageLoaded(true)}
        onError={() => {
          setHasImageError(true);
          setIsImageLoaded(false);
        }}
      />
    </div>
  );

  const detailsPreview =
    renderUri && !hasImageError ? image : noImage;

  const variantsMapped = details?.variants.map((variant) => (
    <PropLibraryInteractionLabel key={variant} interactionName={variant} />
  ));

  const pairIds = details && (
    <div className="flex flex-col w-full gap-[12px]">
      <div className="font-bold text-[16px] text-creator-text">Part IDs</div>
      <div className="grid grid-cols-1 gap-[10px] piLg:grid-cols-2">
        <div className="rounded-[8px] bg-creator-surface/70 px-[12px] py-[10px]">
          <div className="mb-[6px] text-[14px] font-bold text-creator-text">
            Male
          </div>
          <div className="break-all text-[13px] font-medium leading-[1.5] text-creator-text-soft">
            {details.malePartId}
          </div>
        </div>
        <div className="rounded-[8px] bg-creator-surface/70 px-[12px] py-[10px]">
          <div className="mb-[6px] text-[14px] font-bold text-creator-text">
            Female
          </div>
          <div className="break-all text-[13px] font-medium leading-[1.5] text-creator-text-soft">
            {details.femalePartId}
          </div>
        </div>
      </div>
    </div>
  );

  return (
    <div
      className={
        "relative w-full h-full select-none bg-interaction-softBackground rounded-[5px] p-[14px]"
      }
    >
      {details && (
        <div className={"flex overflow-y-auto flex-col gap-[16px]"}>
          <span className={"font-medium text-creator-text text-[22px]"}>
            {details.name}
          </span>
          <div className={"bg-creator-primary-background rounded-[5px] p-[16px] flex"}>
            {detailsPreview}
          </div>
          <div className={"flex flex-col w-full gap-[16px]"}>
            {pairIds}
            <div className={"font-bold text-[16px] text-creator-text"}>
              Variants
            </div>
            <div className={"flex flex-wrap gap-[10px]"}>{variantsMapped}</div>
          </div>
        </div>
      )}
      {details && (
        <div
          className={
            "absolute left-1/2 transform -translate-x-1/2 bottom-[16px] w-[183px] h-[40px]"
          }
        >
          <PiButton variant={"piBlue"} onClick={onSubmit}>
            Change Character Part
          </PiButton>
        </div>
      )}
    </div>
  );
}
